import React, { useState, useEffect, useMemo, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Row,
  RSelect,
} from "../../../components/Component";
import { useHistory } from "react-router-dom";
import { Card, FormGroup, Alert } from "reactstrap";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import swal from "sweetalert";

/**
 * Platform-wide catalog root settings (Settings keys).
 * Not scoped to a partner — edits affect TC specialty trees and clinic price grids globally.
 */

function parentIdOf(cat) {
  if (!cat || cat.parent == null || cat.parent === "") return null;
  if (typeof cat.parent === "object") {
    return cat.parent._id != null ? String(cat.parent._id) : null;
  }
  return String(cat.parent);
}

function isRootCategory(cat) {
  return parentIdOf(cat) == null;
}

function optionFromCategory(c) {
  const rootTag = isRootCategory(c) ? " · root" : "";
  return {
    value: String(c._id),
    label: `${c.label || "Category"} (uid ${c.uid ?? "—"})${rootTag}`,
    uid: c.uid,
    isRoot: isRootCategory(c),
  };
}

const PlatformCatalogRootsPage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({
    tc_specialties_root_category_id: "",
    medical_tourism_catalog_root_category_id: "",
    countries_root_category_id: "",
  });
  const [allCategoryRows, setAllCategoryRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [settingsRes, response] = await Promise.all([
          http.get("/api/v1/settings").catch(() => ({ data: { data: [] } })),
          // Empty parent → top-level roots only (Countries, Teleconsultation, …)
          http.post("/api/v1/category/list", { parent: "" }),
        ]);
        if (cancelled) return;
        const rows = settingsRes.data?.data || [];
        const byKey = {};
        rows.forEach((r) => {
          if (r?.key) byKey[r.key] = r.value != null ? String(r.value).trim() : "";
        });
        setDraft({
          tc_specialties_root_category_id: byKey.tc_specialties_root_category_id || "",
          medical_tourism_catalog_root_category_id: byKey.medical_tourism_catalog_root_category_id || "",
          countries_root_category_id: byKey.countries_root_category_id || "",
        });
        const flat = response.data?.data;
        setAllCategoryRows(Array.isArray(flat) ? flat : []);
      } catch (e) {
        if (!cancelled) {
          swal("Error", e.response?.data?.message || "Failed to load data", "error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const categorySelectOptions = useMemo(() => {
    const roots = allCategoryRows.filter(isRootCategory).map(optionFromCategory);
    roots.sort((a, b) => String(a.label).localeCompare(String(b.label)));

    // Keep a saved non-root value visible if ever set by mistake
    const selectedIds = [
      draft.tc_specialties_root_category_id,
      draft.medical_tourism_catalog_root_category_id,
      draft.countries_root_category_id,
    ]
      .filter(Boolean)
      .map(String);
    const known = new Set(roots.map((o) => o.value));
    for (const id of selectedIds) {
      if (known.has(id)) continue;
      const row = allCategoryRows.find((c) => String(c._id) === id);
      if (row) {
        roots.push(optionFromCategory(row));
        known.add(id);
      }
    }
    return roots;
  }, [allCategoryRows, draft]);

  const countriesUid10 = useMemo(
    () => allCategoryRows.find((c) => Number(c.uid) === 10 && isRootCategory(c)),
    [allCategoryRows]
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await http.put("/api/v1/settings/bulk", {
        settings: [
          {
            key: "tc_specialties_root_category_id",
            value: draft.tc_specialties_root_category_id || "",
            label: "TC specialties root category",
            description:
              "Parent category for teleconsultation specialty list (public API). Admin clinic TC price grid uses the same root (legacy fallback: category uid 192).",
            category: "catalog",
            type: "string",
            isPublic: true,
          },
          {
            key: "medical_tourism_catalog_root_category_id",
            value: draft.medical_tourism_catalog_root_category_id || "",
            label: "Medical tourism catalog root",
            description: "Admin clinic price grid root (legacy fallback: category uid 48)",
            category: "catalog",
            type: "string",
            isPublic: true,
          },
          {
            key: "countries_root_category_id",
            value: draft.countries_root_category_id || "",
            label: "Countries root category",
            description:
              "Parent category for country pickers (second-opinion offers and similar). Legacy fallback: category uid 10 (Countries).",
            category: "catalog",
            type: "string",
            isPublic: true,
          },
        ],
      });
      swal("Success", "Catalog roots saved.", "success");
    } catch (e) {
      swal("Error", e.response?.data?.message || "Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  }, [draft]);

  return (
    <>
      <Head title="Platform catalog roots" />
      <Content>
        <BlockHead size="lg">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h4">Platform catalog roots</BlockTitle>
              <BlockDes>
                <p className="mb-0">
                  Global admin only: pick <strong>top-level</strong> category roots (no parent) — e.g. Teleconsultation,
                  Medical partners, Countries. These trees power specialties, countries (second-opinion offers), and the
                  medical tourism price grid for <strong>all</strong> partners.
                </p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button color="light" size="sm" type="button" onClick={() => history.push(getPublicUrl("specialty-list"))}>
                Open medical specialties tree
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Alert color="info" className="mb-4">
          Partner detail → Configuration lists this screen under <strong>Platform-wide</strong>. Clinic price pages no longer edit these
          roots (single place to avoid confusion). Dropdowns list <strong>root categories only</strong> (parent empty), not
          specialties or treatments.
        </Alert>

        {loading ? (
          <Block>
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          </Block>
        ) : (
          <Block>
            <Card className="card-bordered">
              <div className="card-inner">
                <h6 className="title">Root categories</h6>
                <p className="text-soft small mb-3">
                  Stored as platform settings. Suggested defaults: TC specialties <code>uid 192</code>, medical tourism{" "}
                  <code>uid 48</code>, countries <code>uid 10</code> (Countries).
                </p>
                <Row className="g-gs">
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label" htmlFor="tc-root-select">
                        Teleconsultation specialties root
                      </label>
                      <RSelect
                        inputId="tc-root-select"
                        options={categorySelectOptions}
                        value={
                          categorySelectOptions.find(
                            (o) => String(o.value) === String(draft.tc_specialties_root_category_id)
                          ) || null
                        }
                        onChange={(opt) =>
                          setDraft((prev) => ({
                            ...prev,
                            tc_specialties_root_category_id: opt?.value ? String(opt.value) : "",
                          }))
                        }
                        placeholder="Select root category…"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label" htmlFor="mt-root-select">
                        Medical tourism / treatment price grid root
                      </label>
                      <RSelect
                        inputId="mt-root-select"
                        options={categorySelectOptions}
                        value={
                          categorySelectOptions.find(
                            (o) => String(o.value) === String(draft.medical_tourism_catalog_root_category_id)
                          ) || null
                        }
                        onChange={(opt) =>
                          setDraft((prev) => ({
                            ...prev,
                            medical_tourism_catalog_root_category_id: opt?.value ? String(opt.value) : "",
                          }))
                        }
                        placeholder="Select root category…"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <label className="form-label" htmlFor="countries-root-select">
                        Countries root
                      </label>
                      <RSelect
                        inputId="countries-root-select"
                        options={categorySelectOptions}
                        value={
                          categorySelectOptions.find(
                            (o) => String(o.value) === String(draft.countries_root_category_id)
                          ) || null
                        }
                        onChange={(opt) =>
                          setDraft((prev) => ({
                            ...prev,
                            countries_root_category_id: opt?.value ? String(opt.value) : "",
                          }))
                        }
                        placeholder="Select root category…"
                      />
                      {countriesUid10 &&
                      String(draft.countries_root_category_id) !== String(countriesUid10._id) ? (
                        <Button
                          color="link"
                          size="sm"
                          className="px-0 mt-1"
                          type="button"
                          onClick={() =>
                            setDraft((prev) => ({
                              ...prev,
                              countries_root_category_id: String(countriesUid10._id),
                            }))
                          }
                        >
                          Use Countries (uid 10)
                        </Button>
                      ) : null}
                    </FormGroup>
                  </Col>
                </Row>
                <p className="text-soft small mb-3">
                  Homepage specialties: Home feature column under the teleconsultation specialties root (Medical
                  specialties tree). Jurisdiction country defaults (timezone, currency, languages): open the
                  Jurisdiction root below — not Country regulations (prescription rules).
                </p>
                {(() => {
                  const jur = allCategoryRows.find(
                    (c) => isRootCategory(c) && /^Jurisdiction$/i.test(String(c.label || ""))
                  );
                  if (!jur) return null;
                  return (
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      className="me-2 mb-2"
                      onClick={() =>
                        history.push(
                          `${getPublicUrl("specialty-list")}?parent=${encodeURIComponent(
                            String(jur._id)
                          )}&label=${encodeURIComponent(jur.label || "Jurisdiction")}`
                        )
                      }
                    >
                      Open Jurisdiction tree
                    </Button>
                  );
                })()}
                <Button color="primary" size="sm" type="button" disabled={saving} onClick={save}>
                  {saving ? "Saving…" : "Save catalog roots"}
                </Button>
              </div>
            </Card>
          </Block>
        )}
      </Content>
    </>
  );
};

export default PlatformCatalogRootsPage;
